import { Head, Link, router } from '@inertiajs/react'
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react'
import HeadingSmall from '@/components/heading-small'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AppLayout from '@/layouts/app-layout'
import SettingsLayout from '@/layouts/settings/layout'
import { emailSignaturesPath, newEmailSignaturePath, editEmailSignaturePath, emailSignaturePath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { EmailSignature } from '@/types'

type SignaturesIndexProps = {
  signatures: EmailSignature[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: 'Email Signatures', href: emailSignaturesPath() },
]

export default function SignaturesIndex({ signatures }: SignaturesIndexProps) {
  function handleDelete(signature: EmailSignature) {
    if (confirm(`Delete signature "${signature.text}"?`)) {
      router.delete(emailSignaturePath(signature.id))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Email Signatures" />

      <div className="p-6">
        <SettingsLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <HeadingSmall
              title="Email Signatures"
              description="Manage the signatures used in Digby's email notifications"
            />
            <Button asChild size="sm">
              <Link href={newEmailSignaturePath()}>
                <Plus className="mr-2 h-4 w-4" />
                Add Signature
              </Link>
            </Button>
          </div>

          <div className="space-y-3">
            {signatures.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <p className="text-sm text-muted-foreground">No signatures yet</p>
                <Button asChild className="mt-4" size="sm">
                  <Link href={newEmailSignaturePath()}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add your first signature
                  </Link>
                </Button>
              </div>
            ) : (
              signatures.map((signature) => (
                <div
                  key={signature.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex items-center gap-4">
                    <Badge variant={signature.active ? 'default' : 'secondary'}>
                      {signature.active ? (
                        <Check className="mr-1 h-3 w-3" />
                      ) : (
                        <X className="mr-1 h-3 w-3" />
                      )}
                      {signature.active ? 'Active' : 'Inactive'}
                    </Badge>
                    <span className="italic text-muted-foreground">"{signature.text}"</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" asChild>
                      <Link href={editEmailSignaturePath(signature.id)}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(signature)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </SettingsLayout>
      </div>
    </AppLayout>
  )
}
